import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { apiService, AUTH_SESSION_INVALIDATED_EVENT, getApiBaseUrl } from '../services/apiService';
import { queryClient } from '../queryClient';
import { clearAuthAndTenantStorage, storedSessionMatchesHost } from '../utils/authStorage';
import { buildAppUrl, getHostTenantSlug, isMainDomain } from '../utils/tenantResolver';

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  /** Credential last changed; not bumped for profile-only edits */
  passwordChangedAt?: string;
  organizationId?: number;
  organizationSlug?: string;
  planType?: string;
  role?: string; // User's role in the organization (ADMIN or MEMBER)
  profilePictureUrl?: string;
  backgroundImageUrl?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (userData: User, token: string) => void;
  /** Merge profile fields locally without re-bootstrapping session (avoids profile page flicker). */
  patchUser: (updates: Partial<User>) => void;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean; // Add loading state
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const mapMeRoleToAuthRole = (meRole: string): string => {
  switch (meRole.toUpperCase()) {
    case 'ADMIN_PM':
      return 'ADMIN_PROJECT_MANAGER';
    case 'ADMIN':
      return 'ADMIN';
    case 'PROJECT_MANAGER':
      return 'PROJECT_MANAGER';
    case 'MEMBER':
      return 'MEMBER';
    default:
      return meRole;
  }
};

type OrgSnapshot = { id?: number; slug?: string; planType?: string; role?: string };

const applyOrgSnapshotToUser = (user: User, org: OrgSnapshot): User => {
  let next = { ...user };
  if (typeof org.id === 'number') {
    next = { ...next, organizationId: org.id };
  }
  if (typeof org.slug === 'string' && org.slug.trim()) {
    next = { ...next, organizationSlug: org.slug };
  }
  if (typeof org.planType === 'string' && org.planType.trim()) {
    next = { ...next, planType: org.planType.trim() };
  } else if (typeof org.id === 'number') {
    next = { ...next, planType: 'FREE' };
  }
  if (typeof org.role === 'string' && org.role.trim()) {
    next = { ...next, role: mapMeRoleToAuthRole(org.role) };
  }
  return next;
};

const syncCurrentOrganizationFromUser = (user: User) => {
  if (!user.organizationSlug || !user.organizationId) {
    return;
  }
  const hostSlug = getHostTenantSlug();
  if (hostSlug && user.organizationSlug.trim().toLowerCase() !== hostSlug) {
    return;
  }
  try {
    localStorage.setItem(
      'currentOrganization',
      JSON.stringify({
        id: user.organizationId,
        slug: user.organizationSlug,
        planType: user.planType || 'FREE',
      })
    );
  } catch {
    /* ignore */
  }
};

const AUTH_ACTIVE_SESSION_MARKER_KEY = 'auth:active-session-marker';

const getTabId = (): string => {
  try {
    const existing = sessionStorage.getItem('auth:tab-id');
    if (existing) return existing;
    const next = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem('auth:tab-id', next);
    return next;
  } catch {
    return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
};

const writeActiveSessionMarker = (user: User, tabId: string) => {
  if (typeof user?.id !== 'number') return;
  try {
    localStorage.setItem(
      AUTH_ACTIVE_SESSION_MARKER_KEY,
      JSON.stringify({
        userId: user.id,
        at: Date.now(),
        tabId,
      })
    );
  } catch {
    /* ignore */
  }
};

const readActiveSessionMarker = (raw: string | null): { userId?: number; tabId?: string } | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { userId?: number; tabId?: string };
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const normalizeUserPlanType = (user: User): User => ({
  ...user,
  planType: user.planType?.trim() || 'FREE',
});

/** Refresh role/plan from backend so localStorage never wins over persisted DB values. */
const bootstrapSessionFromBackend = async (
  storedToken: string,
  storedUser: User
): Promise<{ user: User; token: string } | null> => {
  const hostSlug = getHostTenantSlug();
  const headers: Record<string, string> = { Authorization: `Bearer ${storedToken}` };
  if (hostSlug) {
    headers['X-Tenant-Slug'] = hostSlug;
  }

  let user = { ...storedUser };
  let matchedHostOrg = false;

  if (hostSlug) {
    try {
      const orgRes = await fetch(`${getApiBaseUrl()}/organizations`, { headers });
      if (orgRes.ok) {
        const orgs = await orgRes.json();
        if (Array.isArray(orgs)) {
          const match = orgs.find(
            (o: OrgSnapshot) => String(o?.slug || '').toLowerCase() === hostSlug.toLowerCase()
          );
          if (match) {
            matchedHostOrg = true;
            user = applyOrgSnapshotToUser(user, match);
          }
        }
      }
    } catch {
      /* ignore */
    }

    if (!matchedHostOrg) {
      try {
        const primaryRes = await fetch(`${getApiBaseUrl()}/organizations/primary`, { headers });
        if (primaryRes.ok) {
          const primary = (await primaryRes.json()) as OrgSnapshot;
          if (String(primary?.slug || '').toLowerCase() === hostSlug.toLowerCase()) {
            user = applyOrgSnapshotToUser(user, primary);
          }
        }
      } catch {
        /* ignore */
      }
    }
  }

  try {
    const meRes = await fetch(`${getApiBaseUrl()}/user/me`, { headers });
    if (meRes.ok) {
      const me = await meRes.json();
      if (typeof me?.role === 'string' && me.role.trim()) {
        user = { ...user, role: mapMeRoleToAuthRole(me.role) };
      }
      if (typeof me?.planType === 'string' && me.planType.trim()) {
        user = { ...user, planType: me.planType.trim() };
      }
    }
  } catch {
    /* keep organization-derived role if /user/me is temporarily unavailable */
  }

  if (!user.role && storedUser.role) {
    user = { ...user, role: mapMeRoleToAuthRole(storedUser.role) };
  }

  if (hostSlug) {
    const sessionSlug = String(
      user.organizationSlug || storedUser.organizationSlug || hostSlug
    )
      .trim()
      .toLowerCase();
    if (sessionSlug !== hostSlug) {
      return null;
    }
    if (!user.organizationSlug) {
      user = { ...user, organizationSlug: hostSlug };
    }
    if (!user.organizationId && storedUser.organizationId) {
      user = { ...user, organizationId: storedUser.organizationId };
    }
  }

  if (!user.role && hostSlug) {
    return null;
  }

  return { user: normalizeUserPlanType(user), token: storedToken };
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Track loading state
  const tokenRef = useRef<string | null>(null);
  const tabIdRef = useRef<string>(getTabId());
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');

      const clearPostLoginHandoffGrace = () => {
        try {
          sessionStorage.removeItem('auth_handoff_until');
        } catch {
          /* ignore */
        }
      };

      if (!storedUser || !storedToken) {
        clearPostLoginHandoffGrace();
        if (!cancelled) {
          setIsLoading(false);
        }
        return;
      }

      if (!storedSessionMatchesHost(storedUser)) {
        clearAuthAndTenantStorage(queryClient);
        if (!cancelled) {
          setIsLoading(false);
        }
        return;
      }

      if (!storedSessionMatchesHost(storedUser)) {
        clearAuthAndTenantStorage(queryClient);
        if (!cancelled) {
          setIsLoading(false);
        }
        return;
      }

      try {
        const parsed = JSON.parse(storedUser) as User;
        const bootstrapped = await bootstrapSessionFromBackend(storedToken, parsed);
        if (cancelled) {
          return;
        }
        if (!bootstrapped) {
          clearAuthAndTenantStorage(queryClient);
          setIsLoading(false);
          return;
        }
        setUser(bootstrapped.user);
        setToken(bootstrapped.token);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(bootstrapped.user));
        syncCurrentOrganizationFromUser(bootstrapped.user);
        // Login sets auth_handoff_until so axios ignores transient 401s during redirect.
        // Do NOT clear it at init start — that let dashboard requests fire before bootstrap finished
        // and triggered forceClientLogout(SESSION_REPLACED) → bounce back to /login.
        clearPostLoginHandoffGrace();
      } catch (error) {
        console.error('Error restoring session:', error);
        clearAuthAndTenantStorage(queryClient);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void initAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback((userData: User, authToken: string) => {
    const hostSlug = getHostTenantSlug();
    if (!userData.role && hostSlug) {
      console.warn('Login response missing role; session will be completed during bootstrap');
    }
    void (async () => {
      let user = userData;
      let token = authToken;
      const bootstrapped = await bootstrapSessionFromBackend(authToken, userData);
      if (bootstrapped) {
        user = bootstrapped.user;
        token = bootstrapped.token;
      } else {
        user = normalizeUserPlanType(user);
      }
      setUser(user);
      setToken(token);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('token', token);
      syncCurrentOrganizationFromUser(user);
      writeActiveSessionMarker(user, tabIdRef.current);
    })();
  }, []);

  const patchUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => {
      if (!prev) {
        return prev;
      }
      const next = { ...prev, ...updates };
      try {
        localStorage.setItem('user', JSON.stringify(next));
        syncCurrentOrganizationFromUser(next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const logout = useCallback(async () => {
    const t = tokenRef.current;
    if (t) {
      try {
        await apiService.logout();
      } catch (error) {
        console.error('Error calling logout API:', error);
      }
    }

    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    clearAuthAndTenantStorage(queryClient);

    // Full-page navigation before React re-renders unauthenticated shell (otherwise
    // ProtectedRoute sends tenant users to /login on the same host instead of app home).
    if (typeof window !== 'undefined') {
      const homeUrl = !isMainDomain() ? buildAppUrl('/login') : `${window.location.origin}/login`;
      window.location.replace(homeUrl);
      return;
    }
  }, []);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return;
      }
      if (!localStorage.getItem('token')) {
        const homeUrl = !isMainDomain() ? buildAppUrl('/login') : `${window.location.origin}/login`;
        window.location.replace(homeUrl);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Immediate cross-tab logout:
  // if the same account logs in from another tab/session, this tab logs out right away.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_ACTIVE_SESSION_MARKER_KEY) return;
      if (!event.newValue) return;
      if (!isAuthenticated || !user?.id) return;

      const marker = readActiveSessionMarker(event.newValue);
      if (!marker || typeof marker.userId !== 'number') return;
      if (marker.userId !== user.id) return;
      if (marker.tabId === tabIdRef.current) return;

      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
      clearAuthAndTenantStorage(queryClient);
      try {
        sessionStorage.setItem('sessionReplacedRedirectedAt', String(Date.now()));
        sessionStorage.setItem('sessionReplacedNotice', '1');
      } catch {
        /* ignore */
      }
      window.location.replace('/login?sessionReplaced=1');
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isAuthenticated, user?.id]);

  // Detect when the same account signs in elsewhere (server invalidates this session).
  useEffect(() => {
    const onSessionInvalidated = () => {
      setUser(null);
      setToken(null);
      setIsAuthenticated(false);
    };
    window.addEventListener(AUTH_SESSION_INVALIDATED_EVENT, onSessionInvalidated);
    return () => window.removeEventListener(AUTH_SESSION_INVALIDATED_EVENT, onSessionInvalidated);
  }, []);

  const SESSION_HEARTBEAT_MS = 30_000;

  useEffect(() => {
    if (!isAuthenticated || !token) {
      return;
    }

    let cancelled = false;

    const ping = async () => {
      if (cancelled || document.visibilityState !== 'visible') {
        return;
      }
      try {
        await apiService.pingSession();
      } catch {
        /* apiService interceptor redirects on SESSION_REPLACED / auth failures */
      }
    };

    const intervalId = window.setInterval(() => {
      void ping();
    }, SESSION_HEARTBEAT_MS);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void ping();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    void ping();

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isAuthenticated, token]);

  const value: AuthContextType = useMemo(
    () => ({
      user,
      token,
      login,
      patchUser,
      logout,
      isAuthenticated,
      isLoading,
    }),
    [user, token, login, patchUser, logout, isAuthenticated, isLoading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 